import React, { useRef } from "react";
import { format } from "date-fns";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import { getPpeUserProvisions, PpeProvision } from "../lib/api";

interface PpeMaterialRequestNoteDrawerProps {
  anchorProvision: PpeProvision | null;
  onClose: () => void;
}

const formatQuantity = (value: number) =>
  Number.isInteger(value)
    ? String(value)
    : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

const formatItemName = (provision: PpeProvision) => {
  if (!provision.ppe) return "N/A";
  return provision.ppe.size
    ? `${provision.ppe.name} (${provision.ppe.size})`
    : provision.ppe.name;
};

const PpeMaterialRequestNoteDrawer: React.FC<
  PpeMaterialRequestNoteDrawerProps
> = ({ anchorProvision, onClose }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const batchQuery = useQuery({
    queryKey: ["ppe", "provision-batch", anchorProvision?.id],
    queryFn: async () => {
      const response = await getPpeUserProvisions(
        anchorProvision!.ppeUserId,
        1,
        500,
        "",
      );
      return response.data.filter(
        (item) =>
          item.providedDate === anchorProvision!.providedDate &&
          item.providedByUserId === anchorProvision!.providedByUserId,
      );
    },
    enabled: !!anchorProvision,
    placeholderData: keepPreviousData,
  });

  const provisions =
    batchQuery.data && batchQuery.data.length > 0
      ? batchQuery.data
      : anchorProvision
        ? [anchorProvision]
        : [];

  const user = anchorProvision?.ppeUser;
  const providedBy = anchorProvision?.providedByUser?.fullName || "N/A";
  const userName = user?.fullName || "N/A";
  const jobTitle = user?.jobTitle || "N/A";
  const location = user?.location?.trim() || "N/A";

  const displayDate = anchorProvision?.providedDate
    ? format(new Date(anchorProvision.providedDate), "MMM dd, yyyy")
    : "N/A";

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title = `Material Request Note - ${userName}`;
    window.print();

    document.title = originalTitle;
    if (parent) parent.insertBefore(el, next);
    else el.remove();
  };

  return (
    <SidePanel
      open={!!anchorProvision}
      onClose={onClose}
      width="w-[900px]"
      layer="overlay"
    >
      {anchorProvision && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={`Material Request Note for ${userName}`}
            onClose={onClose}
            actions={[
              {
                label: "Print",
                icon: Icons.Print,
                onClick: handlePrint,
                disabled: batchQuery.isLoading,
                pinned: true,
              },
            ]}
          />

          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={
                  isMobile ? "w-full p-3 bg-gray-50" : "w-full p-6 bg-gray-50"
                }
              >
                <ScaledPreview>
                  <div
                    ref={printRef}
                    className="print-area p-4 bg-white shadow-lg rounded-lg"
                  >
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-950 mb-2 border-b-2 border-gray-950 inline-block mx-auto">
                        MATERIAL REQUEST NOTE
                      </h1>
                    </div>
                    <div className="text-center mb-4 border-b-2 border-gray-950 pb-2">
                      <img
                        src="/images/logo.png"
                        alt="Logo"
                        className="h-12 mx-auto mb-2"
                      />
                      <p className="text-sm text-gray-950">
                        Dealers in scaffolding & formwork (sale, rental and
                        fixing, cradle & scissor lift rental and sale)
                      </p>
                      <p className="text-sm text-gray-950">
                        Office: Plot 161, Block 161, Wazo, Tegeta
                      </p>
                      <p className="text-sm text-gray-950">
                        P.O Box 35854, Dar es Salaam, Tanzania. Sales:
                        +255(0)780 973 724 Warehouse: +255(0)780 973 728
                      </p>
                      <p className="text-sm text-gray-950">
                        Email: info@waposcaffolding.com Website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>

                    <div className="mt-3 border border-gray-950 rounded-xl px-4 py-2 text-xs text-gray-950">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                        <p>
                          <span className="font-semibold">Date:</span>{" "}
                          <span className="font-medium">{displayDate}</span>
                        </p>
                        <p>
                          <span className="font-semibold">Issued By:</span>{" "}
                          <span className="font-medium">{providedBy}</span>
                        </p>
                        <p>
                          <span className="font-semibold">Requested By:</span>{" "}
                          <span className="font-medium">{userName}</span>
                        </p>
                        <p>
                          <span className="font-semibold">Location:</span>{" "}
                          <span className="font-medium">{location}</span>
                        </p>
                        <p className="col-span-2">
                          <span className="font-semibold">Job Title:</span>{" "}
                          <span className="font-medium">{jobTitle}</span>
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="text-center text-gray-950 font-semibold mb-2">
                        PPE / NON SCAFFOLDS REQUESTED
                      </div>

                      <table className="w-full border border-gray-950 text-sm">
                        <thead>
                          <tr className="bg-gray-950">
                            <th className="text-white p-2 w-[70px] text-left">
                              No:
                            </th>
                            <th className="text-white p-2 text-left">Item</th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Qty
                            </th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Unit
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {provisions.length > 0 ? (
                            provisions.map((item, idx) => (
                              <tr key={item.id}>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {String(idx + 1).padStart(2, "0")}.
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {formatItemName(item)}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {formatQuantity(Number(item.quantity))}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {item.ppe?.unit || "—"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={4}
                                className="border border-gray-950 px-2 py-6 text-center text-gray-950"
                              >
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-10 grid grid-cols-2 gap-6 text-xs text-gray-950">
                      <div className="space-y-3">
                        <p className="font-semibold">RECEIVED BY:</p>
                        <p>Name: {userName}</p>
                        <p>Signature: ________________________</p>
                        <p>Date: _____________________________</p>
                      </div>
                      <div className="space-y-3">
                        <p className="font-semibold">ISSUED BY:</p>
                        <p>Name: {providedBy}</p>
                        <p>Signature: ________________________</p>
                        <p>Date: _____________________________</p>
                      </div>
                    </div>

                    <div className="flex justify-between mt-8 text-xs text-gray-950">
                      <p>
                        Prepared by:{" "}
                        <span className="font-semibold">{providedBy}</span>
                      </p>
                      <p>
                        Print Date:{" "}
                        <span className="font-semibold">
                          {format(new Date(), "MMM dd, yyyy")}
                        </span>
                      </p>
                    </div>
                  </div>
                </ScaledPreview>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default PpeMaterialRequestNoteDrawer;
